import type {Object3D} from 'three/webgpu';

/**
 * 装配组件位姿写回后的派生副作用协调器。
 *
 * 设计说明：
 * 1. 业务组件 Object3D 始终是位姿真相，本类只在业务写回完成后刷新派生状态；
 * 2. 装配体基准重算、实例矩阵上传与渲染请求必须保持固定顺序，避免不同命令各自遗漏；
 * 3. 本类不保存历史、不修改 Mate Definition，也不读取 GPU instanceId。
 */
export class AssemblyComponentTransformEffectsCoordinator {
  /**
   * 创建装配组件位姿副作用协调器。
   *
   * @param host Viewer 提供的最小派生投影端口。
   */
  public constructor(
    private readonly host: PuppyCAD.AssemblyComponentTransformEffectsHost,
  ) {}

  /**
   * 同步一批已经完成业务位姿写回的组件对象。
   *
   * @param changedObjects 本批实际发生变化的组件 transform 根。
   * @param options 派生副作用配置。
   */
  public synchronize(
    changedObjects: readonly Object3D[],
    options?: PuppyCAD.AssemblyComponentTransformEffectsOptions,
  ): void {
    if (changedObjects.length === 0) {
      return;
    }

    // 派生基准必须先消费新的业务位姿，后续选择和配合才能读取同一帧的权威几何。
    if (options?.recomputeAssemblyReferences !== false) {
      this.host.recomputeAssemblyReferences();
    }

    // 实例矩阵只更新反向索引命中的槽位，不允许在此处扫描场景或全量同步。
    this.host.updateImportedModelInstanceMatrices(changedObjects);

    // 一批位姿副作用全部完成后最多渲染一次，避免预览高频路径重复绘制中间状态。
    if (options?.requestRender !== false) {
      this.host.requestRender();
    }
  }
}
